<template>
  <div class="card">
    <div class="card-body">
      <h5 class="fw-bold mb-3">Choose Target</h5>
      <div class="mb-3">
        <div class="form-check">
          <input
            id="target-incident"
            v-model="kind"
            class="form-check-input border border-primary"
            type="radio"
            value="incident"
          />
          <label class="form-check-label" for="target-incident">Incident</label>
        </div>
        <div class="form-check">
          <input
            id="target-classification"
            v-model="kind"
            class="form-check-input border border-primary"
            type="radio"
            value="classification"
          />
          <label class="form-check-label" for="target-classification">Classification</label>
        </div>
      </div>

      <div v-if="kind === 'incident'" class="mb-3">
        <label for="incident-select" class="form-label">Incident</label>
        <select
          id="incident-select"
          v-model="incidentId"
          class="form-select border border-primary"
          :disabled="loadingIncidents"
        >
          <option value="" disabled>
            {{ loadingIncidents ? 'Loading incidents…' : '— Select Incident —' }}
          </option>
          <option v-for="incident in incidents" :key="incident.id" :value="incident.id">
            {{ incident.name }}
          </option>
        </select>
        <div
          v-if="incidentsError"
          class="alert alert-danger outline small d-flex align-items-center mt-2 mb-0"
        >
          <span class="flex-grow-1">{{ incidentsError }}</span>
          <button class="btn btn-sm btn-outline-danger ms-2" @click="loadIncidents">
            <i class="fa fa-refresh me-1"></i>Retry
          </button>
        </div>
        <div v-else-if="!loadingIncidents && incidents.length === 0" class="form-text">
          No open incidents. Create one on the Incidents page first.
        </div>
      </div>

      <div v-if="kind === 'classification'" class="mb-3">
        <label class="form-label">Classification</label>
        <div class="d-flex gap-3 flex-wrap">
          <div v-for="c in assignable" :key="c.value" class="form-check">
            <input
              :id="`cls-${c.value}`"
              v-model="classification"
              class="form-check-input border border-primary"
              type="radio"
              :value="c.value"
            />
            <label class="form-check-label" :for="`cls-${c.value}`">{{ c.label }}</label>
          </div>
        </div>
      </div>

      <div class="mb-3">
        <label for="reason" class="form-label">Reason (≥10 chars)</label>
        <textarea
          id="reason"
          v-model="reason"
          class="form-control border border-primary"
          rows="2"
        />
      </div>

      <button class="btn btn-primary" :disabled="!isValid" @click="emit('next', built)">
        Continue
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { apiClient, API_PATHS } from '@/services/api';
import type { AssignmentTarget } from '../composables/useAssignmentSession';

const emit = defineEmits<{
  (e: 'next', payload: { target: AssignmentTarget; reason: string }): void;
}>();

const kind = ref<'incident' | 'classification'>('incident');
const incidentId = ref('');
const incidents = ref<Array<{ id: string; name: string }>>([]);
const loadingIncidents = ref(false);
const classification = ref<'standard' | 'overshoot' | 'ev_reconcile' | 'drive_away'>('standard');
const reason = ref('');
const assignable = [
  { value: 'standard', label: 'Standard' },
  { value: 'overshoot', label: 'Overshoot' },
  { value: 'ev_reconcile', label: 'EV Reconcile' },
  { value: 'drive_away', label: 'Drive Away' },
] as const;

const incidentsError = ref('');

async function loadIncidents() {
  loadingIncidents.value = true;
  incidentsError.value = '';
  try {
    const response = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.payOperations.orderAmendments.incidents,
      { params: { status: 'open' } },
    );
    incidents.value = response.data?.items ?? [];
  } catch (err: any) {
    incidentsError.value =
      err?.response?.data?.message || 'Failed to load open incidents. Try again.';
  } finally {
    loadingIncidents.value = false;
  }
}

onMounted(loadIncidents);

const isValid = computed(() => {
  if (reason.value.trim().length < 10) return false;
  if (kind.value === 'incident') return !!incidentId.value;
  return !!classification.value;
});

const built = computed<{ target: AssignmentTarget; reason: string }>(() => {
  if (kind.value === 'incident') {
    const inc = incidents.value.find((i) => i.id === incidentId.value);
    return {
      target: { kind: 'incident', incidentId: incidentId.value, incidentName: inc?.name ?? '' },
      reason: reason.value.trim(),
    };
  }
  return {
    target: { kind: 'classification', classification: classification.value },
    reason: reason.value.trim(),
  };
});
</script>
